import React, { useEffect } from 'react'
import { Card, Button, message, notification } from 'antd'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import './ApplicationSelector.css'

// Configure notification style
notification.config({
  placement: 'topRight',
  top: 70,
  duration: 3,
})
import {
  fetchApplications,
  setSelectedApplication,
} from '../features/application/applicationSlice'
import { fetchDependencies, clearDependencies } from '../features/dependencies/dependenciesSlice'
import { fetchDatabaseDetails, clearDatabaseInfo } from '../features/database/databaseSlice'
import { fetchJobs, clearJobs } from '../features/jobs/jobsSlice'
import { fetchServices, clearServices } from '../features/services/servicesSlice'
import FormikSelect from './FormikSelect'
import { SearchOutlined } from '@ant-design/icons'
import { fetchPipelines, clearPipelines } from '../features/pipeline/pipelineSlice'

const ApplicationSelector = ({ summaryCards }) => {
  const dispatch = useDispatch()
  const { applications, selectedApplication, isLoading } = useSelector(
    (state) => state.application
  )

  useEffect(() => {
    dispatch(fetchApplications())
  }, [dispatch])

  const handleFetchDetails = async (values) => {
    if (!values.applicationId) {
      notification.error({
        message: 'Validation Error',
        description: 'Please select an application',
        style: {
          background: '#000',
          color: '#fff',
          border: '2px solid #ff4d4f',
        },
      })
      return
    }
    
    try {
      // Clear previous data
      dispatch(clearDatabaseInfo())
      dispatch(clearDependencies())
      dispatch(clearJobs())
      dispatch(clearServices())
      dispatch(clearPipelines())
      dispatch(setSelectedApplication(values.applicationId))
      
      // Get selected application to access database name and APMID
      const selectedApp = applications.find(app => app.applicationId === values.applicationId)
      const databaseName = selectedApp?.applicationDatabase || selectedApp?.databaseName
      const applicationAPMID = selectedApp?.applicationAPMID
      
      console.log('Selected Application:', selectedApp)
      console.log('Database Name:', databaseName)
      console.log('Application APMID:', applicationAPMID)
      
      // Fetch all details in parallel
      const fetchPromises = []
      
      // Fetch services using applicationAPMID
      if (applicationAPMID) {
        console.log('Fetching services for:', applicationAPMID)
        fetchPromises.push(dispatch(fetchServices(applicationAPMID)))
      } else {
        console.warn('No application APMID found for selected application')
      }
      
      // Fetch dependencies, database details, and jobs using database name
      if (databaseName) {
        console.log('Fetching dependencies for:', databaseName)
        console.log('Fetching database details for:', databaseName)
        console.log('Fetching jobs for:', databaseName)
        fetchPromises.push(dispatch(fetchDependencies(databaseName)))
        fetchPromises.push(dispatch(fetchDatabaseDetails(databaseName)))
        fetchPromises.push(dispatch(fetchJobs(databaseName)))
        fetchPromises.push(dispatch(fetchPipelines(databaseName)))
      } else {
        console.warn('No database name found for selected application')
      }
      
      await Promise.all(fetchPromises)
      
      notification.success({
        message: 'Success',
        description: 'Application details fetched successfully',
        style: {
          background: '#000',
          color: '#fff',
          border: '2px solid #FFE600',
        },
      })
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch application details',
        style: {
          background: '#000',
          color: '#fff',
          border: '2px solid #ff4d4f',
        },
      })
    }
  }

  const applicationOptions = applications.map((app) => ({
    label: app.applicationName,
    value: app.applicationId,
  }))

  return (
    <div style={{ marginBottom: 24 }}>
      <Card 
        style={{ 
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          marginBottom: summaryCards ? 0 : 24
        }}
      >
        <Formik
          initialValues={{
            applicationId: selectedApplication || '',
          }}
          onSubmit={handleFetchDetails}
          enableReinitialize
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form>
              <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '500px' }}>
                  <FormikSelect
                    name="applicationId"
                    label="Select Application:"
                    placeholder="Please select application"
                    options={applicationOptions}
                    loading={isLoading}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      htmlType="submit"
                      loading={isSubmitting}
                      disabled={!values.applicationId || isSubmitting}
                      icon={<SearchOutlined />}
                      className="fetch-button"
                      size="small"
                      style={{
                        background: values.applicationId ? '#000' : '#666',
                        color: '#fff',
                        border: 'none',
                        padding: '4px 16px',
                        height: '28px',
                        fontSize: '12px',
                        fontWeight: '500',
                        borderRadius: '4px',
                        cursor: values.applicationId ? 'pointer' : 'not-allowed',
                      }}
                      onMouseEnter={(e) => {
                        if (values.applicationId) {
                          e.currentTarget.style.background = '#FFE600'
                          e.currentTarget.style.color = '#000'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (values.applicationId) {
                          e.currentTarget.style.background = '#000'
                          e.currentTarget.style.color = '#fff'
                        }
                      }}
                    >
                      Fetch Details
                    </Button>
                  </div>
                </div>
                {summaryCards && (
                  <div style={{ flex: 1 }}>
                    {summaryCards}
                  </div>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </Card>
    </div>
  )
}

export default ApplicationSelector
